import { useCallback, useEffect, useState } from "react";
import jwtDecode from "jwt-decode";

/**
 * Ambiente simulado da conta de teste `oficial_teste_gamefik`.
 *
 * A conta teste precisa navegar por TODOS os fluxos do produto (novo design,
 * design antigo, páginas legadas como /details "Lançamentos") sem ficar presa
 * aos redirects de coorte. Este módulo guarda um override local (por browser)
 * do modo de UI, consumido por `useNewUi` e `useLegacyRouteRedirect`:
 *
 * - "auto":   comportamento real da conta (NMG/flag PostHog) — só desliga os
 *             redirects de página legada, para poder entrar em qualquer rota.
 * - "new":    força o design novo (isNewUi = true).
 * - "legacy": força o design antigo (isNewUi = false).
 *
 * Só tem efeito quando a conta da sessão é a oficial_teste_gamefik.
 */

export const OFFICIAL_TEST_WORLD = "oficial_teste_gamefik";
export const OFFICIAL_TEST_ACCOUNT_ID = "6a610e9f55f9a80611b94db4";

type AccountIdentity = {
  _id?: unknown;
  name?: unknown;
  world?: unknown;
  "new-manager-global"?: unknown;
};

type SessionClaims = {
  accountId?: unknown;
  world?: unknown;
};

/**
 * A API guarda o identificador Mongo em `world` e o slug legível em `name`.
 * O fallback por `world` preserva sessões antigas que ainda trazem o slug ali.
 */
export function isOfficialTestAccount(account?: AccountIdentity): boolean {
  return account?.name === OFFICIAL_TEST_WORLD || account?.world === OFFICIAL_TEST_WORLD;
}

/**
 * Libera somente a sessão real da conta oficial. O cache identifica a conta
 * exibida, enquanto os claims vinculam esse cache ao token atual; qualquer
 * ausência, corrupção ou divergência mantém o gate legado ativo.
 */
export function isOfficialTestAccountSession(
  account: AccountIdentity | undefined,
  token: string | null | undefined,
): boolean {
  if (
    account?._id !== OFFICIAL_TEST_ACCOUNT_ID
    || account.name !== OFFICIAL_TEST_WORLD
    || account.world !== OFFICIAL_TEST_WORLD
    || typeof token !== "string"
    || !token
  ) return false;

  try {
    const claims = jwtDecode<SessionClaims>(token);
    return claims.accountId === OFFICIAL_TEST_ACCOUNT_ID
      && claims.world === OFFICIAL_TEST_WORLD;
  } catch {
    return false;
  }
}

type LegacyRouteRedirectState = {
  target: string | null;
  account?: AccountIdentity;
  isLoading: boolean;
  isNewUi: boolean;
};

export function shouldRedirectLegacyRoute({
  target,
  account,
  isLoading,
  isNewUi,
}: LegacyRouteRedirectState): boolean {
  return Boolean(
    target && account && !isLoading && isNewUi && !isOfficialTestAccount(account)
  );
}

export type TestUiMode = "auto" | "new" | "legacy";

const STORAGE_KEY = "gamefik.testAccount.uiMode";
const CHANGE_EVENT = "gamefik:test-account-ui-mode-changed";

export function getTestUiMode(): TestUiMode {
  if (typeof window === "undefined") return "auto";
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return raw === "new" || raw === "legacy" ? raw : "auto";
}

export function setTestUiMode(mode: TestUiMode): void {
  if (typeof window === "undefined") return;
  if (mode === "auto") {
    window.localStorage.removeItem(STORAGE_KEY);
  } else {
    window.localStorage.setItem(STORAGE_KEY, mode);
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/** Modo atual + setter reativos (sincroniza entre componentes e abas). */
export function useTestUiMode(): [TestUiMode, (mode: TestUiMode) => void] {
  const [mode, setMode] = useState<TestUiMode>("auto");

  useEffect(() => {
    const sync = () => setMode(getTestUiMode());
    sync();
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const update = useCallback((next: TestUiMode) => {
    setTestUiMode(next);
    setMode(next);
  }, []);

  return [mode, update];
}
